import { ModalAction, MODAL_GLYPH_PROPS, ModalTitle } from '@overlayqa/shared/ui';
import { useEffect, useRef, useState } from 'react';
import { useModalA11y } from '@/hooks/useModalA11y';
import { useNavigate } from 'react-router-dom';
import { SparklesIcon, ThumbsUpIcon, ThumbsDownIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useCurrentWhatsNew, useMarkWhatsNewRead } from '../hooks/useWhatsNew';
import { toast } from 'sonner';
import { capture } from '@/lib/analytics';
import type { WhatsNewItem } from '../lib/api/whatsNew';
import { ModalPortal } from '@/components/shared/ModalPortal';

type DismissMethod = 'got_it' | 'x' | 'esc' | 'backdrop';
type Rating = 'positive' | 'negative';

/** Show at most this many "Also new" items (template rule, Emily 2026-10-02). */
const MAX_ITEMS = 4;

/** Renders **word** as bold. Text nodes only: release copy is never parsed as HTML. */
function withBold(text: string) {
  return text.split(/\*\*(.+?)\*\*/g).map((part, i) => (i % 2 ? <strong key={i}>{part}</strong> : part));
}

/**
 * Curated "What's New" modal. Escapable, rendered after the shell paints
 * (the query resolves after mount, so it appears over the user's dashboard).
 * Mounted once in Layout, next to UpgradeModal.
 *
 * Visibility = `release` present AND not locally dismissed. The mark-read
 * mutation does not refetch /current, so a local `dismissed` latch is what
 * actually closes the modal on any dismiss path (mirrors UpgradeModal's
 * local `setBlock(null)` — deriving visibility from the query alone would
 * leave the modal on screen after "Got it"/✕/Esc/backdrop/in-app-CTA, i.e.
 * the exact trap D7 forbids).
 */
export function WhatsNewModal() {
  const { data: release } = useCurrentWhatsNew();
  const markRead = useMarkWhatsNewRead();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  const [rating, setRating] = useState<Rating | null>(null);
  const headlineRef = useRef<HTMLHeadingElement>(null);
  const shownFor = useRef<string | null>(null);
  const restoreFocusTo = useRef<Element | null>(null);
  // Trap target. This component already restores focus itself (see close()),
  // so the hook's own restore lands on the same element and is a no-op here.
  const dialogRef = useRef<HTMLDivElement>(null);
  const releaseId = release?.id ?? null;

  // Handlers defined before the effects so the Esc effect can close over `close`.
  // They guard on `release` so they are safe to define above the early return
  // (Rules of Hooks require all hooks — incl. effects — before any return).
  const finish = () => {
    if (!release) return;
    markRead.mutate(release.id, {
      onError: () => {
        // Dismissal itself is fine; be honest that it may resurface.
        toast.error("Couldn't save that you've seen this update. It may reappear.");
      },
    });
    setDismissed(true);
    (restoreFocusTo.current as HTMLElement | null)?.focus?.();
  };

  const close = (method: DismissMethod) => {
    if (!release) return;
    capture('whats_new_dismissed', { release_id: release.id, method });
    finish();
  };

  const routeTo = (url: string) => {
    if (url.startsWith('/')) navigate(url);
    else window.open(url, '_blank', 'noopener');
  };

  const onCta = () => {
    if (!release?.ctaUrl) return;
    capture('whats_new_cta_clicked', { release_id: release.id });
    routeTo(release.ctaUrl);
    finish();
  };

  // Thumbs feedback. Records the vote only; the modal stays open so it never feels like a second step.
  const rate = (value: Rating) => {
    if (!release) return;
    setRating(value);
    capture('whats_new_feedback', { release_id: release.id, rating: value });
  };

  const onItem = (item: WhatsNewItem) => {
    if (item.url) routeTo(item.url);
  };

  // If a different release arrives, re-arm (a returning session may surface a newer one).
  useEffect(() => { setDismissed(false); setRating(null); }, [releaseId]);

  // whats_new_shown — exactly once per release, only when it actually renders.
  useEffect(() => {
    if (!release || dismissed || shownFor.current === release.id) return;
    shownFor.current = release.id;
    restoreFocusTo.current = document.activeElement;
    capture('whats_new_shown', { release_id: release.id, release_title: release.title });
  }, [release, dismissed]);

  // Open on the headline, not the close X. Declared after the shown effect (which
  // captures the opener) and before useModalA11y, whose open effect skips when focus
  // is already inside (useModalA11y.ts: "unless something in there already has it").
  useEffect(() => {
    if (!release || dismissed) return;
    headlineRef.current?.focus({ preventScroll: true });
  }, [release, dismissed]);

  // Esc closes, focus is trapped, and focus returns to the trigger on close.
  // The 'esc' reason is preserved for the dismissal analytics.
  useModalA11y(Boolean(release) && !dismissed, dialogRef, () => close('esc'));

  if (!release || dismissed) return null;

  const overlay = (
    <div
      ref={dialogRef}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="whats-new-title"
      onClick={(e) => { if (e.target === e.currentTarget) close('backdrop'); }}
    >
      <section className="bg-white rounded-xl w-full max-w-lg shadow-xl overflow-hidden">
        <header className="px-6 py-2 border-b border-gray-100 flex items-center justify-between">
          <ModalTitle icon={SparklesIcon} as="h1" id="whats-new-title" className="oq-dialog__title">
              {release.title} · {release.dateLabel}
            </ModalTitle>
          <button
            type="button"
            onClick={() => close('x')}
            aria-label="Dismiss"
            className="text-gray-400 hover:text-gray-600 transition-colors p-2"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </header>

        <div className="px-6 pt-4 pb-5">
          {/* The hero is always the brand-gradient illustration, never a screenshot (Emily, 2026-10-02). */}
          <div className="whats-new-hero" aria-hidden="true" />

          <h2
            ref={headlineRef}
            tabIndex={-1}
            className="mt-6 text-2xl leading-tight font-bold text-gray-900 focus:outline-none [text-wrap:balance]"
          >
            {release.headline}
          </h2>
          <p className="modal-intro mt-2">{withBold(release.body)}</p>

          {release.items.length > 0 && (
            <>
              <p className="section-label mt-4">Also new</p>
              <ul className="mt-2 space-y-1">
                {release.items.slice(0, MAX_ITEMS).map((item, i) => (
                  <li key={i} className="text-sm text-gray-700 line-clamp-2 [text-wrap:pretty]">
                    {item.url ? (
                      <button type="button" className="text-primary-600 hover:underline" onClick={() => onItem(item)}><ModalAction kind="open">
                        {withBold(item.text)}
                      </ModalAction></button>
                    ) : (
                      withBold(item.text)
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}

          <div className="mt-6 flex items-end gap-3">
            <div className="mr-auto flex gap-1" role="group" aria-label="Was this update useful?">
              {([['positive', 'Helpful', ThumbsUpIcon], ['negative', 'Not helpful', ThumbsDownIcon]] as const).map(([value, label, icon]) => (
                <button
                  key={value}
                  type="button"
                  aria-label={label}
                  title={label}
                  aria-pressed={rating === value}
                  onClick={() => rate(value)}
                  className={`grid place-items-center w-7 h-7 rounded-md transition-colors ${rating === value ? 'text-primary-600 bg-primary-50' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'}`}
                >
                  <HugeiconsIcon icon={icon} {...MODAL_GLYPH_PROPS} color="currentColor" aria-hidden="true" />
                </button>
              ))}
            </div>
            {release.ctaUrl ? (
              <button type="button" className="btn-primary" onClick={onCta}><ModalAction kind="next">
                {release.ctaLabel}
              </ModalAction></button>
            ) : (
              <button type="button" className="btn-primary" onClick={() => close('got_it')}><ModalAction kind="done">
                Got it
              </ModalAction></button>
            )}
          </div>
        </div>
      </section>
    </div>
  );

  return <ModalPortal>{overlay}</ModalPortal>;
}
